import { useEffect, useRef, type ReactElement, type RefObject } from "react";
import { Users } from "lucide-react";
import { color, radius, space, motion as motionTokens } from "@tokens/index";
import { Popover, Text } from "../primitives";
import type { QueueSummary } from "../../experience-shell/queues/types";
import { formatAncestorPath, type QueueNameResolver } from "./workflowRecipients";

export interface MentionPickerProps {
    readonly open: boolean;
    /** Element the list floats above. The composer well. */
    readonly anchorRef: RefObject<HTMLElement | null>;
    /** Queues that match the current "@" filter. The composer computes them. */
    readonly candidates: readonly QueueSummary[];
    /** Names for the ids in each candidate's `ancestorPath`. */
    readonly resolveQueueName: QueueNameResolver;
    /** True while the queue tree has not arrived. */
    readonly loading: boolean;
    /** True when the "@" filter has text. Drives the empty-state wording. */
    readonly hasFilter: boolean;
    /** Index of the candidate that arrow keys have moved to. */
    readonly activeIndex: number;
    readonly onActiveIndexChange: (index: number) => void;
    readonly onPick: (queue: QueueSummary) => void;
    readonly onDismiss: () => void;
    /**
     * When false the list still opens so the user can browse, and a banner
     * explains that no thread can start from this context. The composer
     * repeats the notice on send.
     */
    readonly spawnEnabled?: boolean;
}

/**
 * Recipient list for the "@" mention in the start-thread composer. The
 * composer input keeps focus; this list only follows `activeIndex`.
 */
export function MentionPicker({
    open,
    anchorRef,
    candidates,
    resolveQueueName,
    loading,
    hasFilter,
    activeIndex,
    onActiveIndexChange,
    onPick,
    onDismiss,
    spawnEnabled = true,
}: MentionPickerProps): ReactElement | null {
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const active = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
        active?.scrollIntoView({ block: "nearest" });
    }, [open, activeIndex]);

    return (
        <Popover
            open={open}
            onClose={onDismiss}
            anchorRef={anchorRef}
            placement="top-start"
            matchAnchorWidth
            role="listbox"
            initialFocus="none"
            returnFocus={false}
            maxHeight={260}
        >
            <div
                ref={listRef}
                aria-label="Pick a recipient queue"
                style={{ padding: space[1] }}
                onMouseDown={(e) => {
                    // The composer input keeps focus so typing keeps filtering.
                    e.preventDefault();
                }}
            >
                {!spawnEnabled && (
                    <div
                        role="note"
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: space[2],
                            padding: `${space[2]} ${space[3]}`,
                            marginBottom: space[1],
                            borderRadius: radius.sm,
                            background: color.status.warningBg,
                        }}
                    >
                        <Text role="caption" tone="warning">
                            Spawn needs a queue context. Open this workflow from one of your queues to
                            enable @-mention sending. You can still browse below.
                        </Text>
                    </div>
                )}
                {candidates.length === 0 ? (
                    <div
                        style={{
                            padding: `${space[3]} ${space[3]}`,
                            display: "flex",
                            alignItems: "center",
                            gap: space[2],
                        }}
                    >
                        <Text role="caption" tone="subtle">
                            {loading ? "Loading queues…" : hasFilter ? "No matching queues" : "No workable queues"}
                        </Text>
                    </div>
                ) : (
                    candidates.map((q, index) => {
                        const active = index === activeIndex;
                        const path = formatAncestorPath(q.ancestorPath, resolveQueueName);
                        return (
                            <button
                                key={q.id}
                                type="button"
                                role="option"
                                aria-selected={active}
                                tabIndex={-1}
                                onClick={() => onPick(q)}
                                onMouseEnter={() => onActiveIndexChange(index)}
                                style={{
                                    appearance: "none",
                                    width: "100%",
                                    minHeight: 40,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: space[2],
                                    padding: `${space[2]} ${space[3]}`,
                                    background: active ? color.bg.selected : "transparent",
                                    border: "none",
                                    borderRadius: radius.sm,
                                    cursor: "pointer",
                                    color: color.fg.default,
                                    textAlign: "left",
                                    transition: `background ${motionTokens.durations.fast} ${motionTokens.easings.standard}`,
                                }}
                            >
                                <Users
                                    size={14}
                                    strokeWidth={2}
                                    style={{ color: color.accent.primary, flexShrink: 0 }}
                                    aria-hidden="true"
                                />
                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                    <Text role="body" weight="medium" truncate>
                                        {q.name}
                                    </Text>
                                    {path !== null && (
                                        <Text role="caption" tone="subtle" truncate>
                                            {path}
                                        </Text>
                                    )}
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </Popover>
    );
}
